import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const TermsAndConditions = () => {
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-800">
            {/* Header - Reused from LandingPage/ContactUs styles */}
            <motion.header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white/95 backdrop-blur-md shadow-lg"
                    }`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-3xl font-bold text-ayurmax-primary"
                        >
                            <Link to="/" className="flex items-center">
                                <motion.img
                                    whileHover={{ scale: 1.05 }}
                                    src="/Ayurmaxfinal.png"
                                    alt="Ayumax"
                                    className="h-16 md:h-20"
                                />
                            </Link>
                        </motion.div>
                        <nav className="hidden md:flex items-center space-x-8">
                            <Link to="/" className="hover:text-ayurmax-primary transition-colors text-gray-700">Home</Link>
                            <Link to="/contact-us" className="hover:text-ayurmax-primary transition-colors text-gray-700">Contact</Link>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate("/login")}
                                className="px-6 py-2 rounded-full font-semibold transition-all bg-ayurmax-primary text-white hover:bg-ayurmax-dark shadow-lg"
                            >
                                Login
                            </motion.button>
                        </nav>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate("/login")}
                            className="md:hidden px-4 py-2 bg-ayurmax-primary text-white rounded-full font-semibold text-sm shadow-lg"
                        >
                            Login
                        </motion.button>
                    </div>
                </div>
            </motion.header>

            {/* Main Content */}
            <main className="flex-1 pt-32 pb-16 px-4 sm:px-6 lg:px-8 container mx-auto max-w-4xl">
                <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100">
                    <h1 className="text-3xl md:text-5xl font-bold text-ayurmax-primary mb-6">Terms and Conditions</h1>
                    <p className="text-gray-500 mb-8 italic">Last Updated: March 2026</p>

                    <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
                        <p>
                            These Terms govern the use of Aseetsoft software and www.aseetsoft.com.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Nature of Service</h3>
                        <p>
                            Aseetsoft is a clinic management software platform. PIT Labs does not provide medical advice.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. User Eligibility</h3>
                        <p>
                            Users must be authorized medical practitioners or clinic staff.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Account Responsibility</h3>
                        <p>
                            Users must maintain login confidentiality.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Medical Disclaimer</h3>
                        <p>PIT Labs is not responsible for:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Diagnosis decisions</li>
                            <li>Prescriptions</li>
                            <li>Treatment outcomes</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Data Disclaimer</h3>
                        <p>PIT Labs is not responsible for:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Incorrect user data</li>
                            <li>Data loss due to misuse</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Service Availability</h3>
                        <p>
                            Service interruptions may occur.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. Limitation of Liability</h3>
                        <p>PIT Labs shall not be liable for:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Data loss</li>
                            <li>Business losses</li>
                            <li>Medical disputes</li>
                        </ul>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. Intellectual Property</h3>
                        <p>
                            All software belongs to PIT Labs.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">9. Termination</h3>
                        <p>
                            Accounts may be suspended for misuse.
                        </p>

                        <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">10. Governing Law</h3>
                        <p>
                            Indian law applies.<br />
                            Jurisdiction: Pune, Maharashtra.
                        </p>

                        <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="font-bold text-gray-900 mb-2">Contact:</p>
                            <a href="mailto:reachus@pitlabs.in" className="text-ayurmax-primary hover:underline font-medium">reachus@pitlabs.in</a>
                        </div>
                    </div>
                </div>
            </main>

            {/* Short Footer */}
            <footer className="bg-ayurmax-dark text-white py-8 text-center mt-auto">
                <p className="text-white/50">&copy; 2026 PIT Labs. All Rights Reserved.</p>
            </footer>
        </div>
    );
};

export default TermsAndConditions;
